/**
 * KERVA Butter-Smooth Lazy-Load Motion Observer
 * Ultra-fluid native IntersectionObserver + Scroll Dynamics + Tactile Interactions.
 * 60fps GPU performance, zero external libraries.
 */

(function () {
  'use strict';

  if (typeof window === 'undefined') return;

  const isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Initializes or updates the observer on reveal elements
   */
  function initMotionObserver() {
    // Auto-discover candidate sections, product grids, and cards across all pages including PDP and home
    const autoCandidates = document.querySelectorAll(
      '.section-header:not([data-reveal]), .section:not([data-reveal]), .section-wrapper:not([data-reveal]), .product-information:not([data-reveal]), .product-recommendations:not([data-reveal]), product-media-gallery:not([data-reveal]), .product-details:not([data-reveal]), .product-grid:not([data-reveal-group]), .product-card:not([data-reveal]), .editorial-card:not([data-reveal]), slideshow-component:not([data-reveal]), .custom-section-content:not([data-reveal])'
    );
    autoCandidates.forEach((el) => {
      if (!el.hasAttribute('data-reveal')) {
        el.setAttribute('data-reveal', '');
      }
    });

    const revealElements = document.querySelectorAll(
      '[data-reveal]:not(.is-revealed), .reveal-on-scroll:not(.is-revealed), .motion-reveal:not(.is-revealed)'
    );

    if (revealElements.length === 0) return;

    if (isReducedMotion || !('IntersectionObserver' in window)) {
      revealElements.forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            obs.unobserve(entry.target);
          }
        });
      },
      {
        root: null,
        rootMargin: '0px 0px -40px 0px',
        threshold: 0.06,
      }
    );

    revealElements.forEach((el) => {
      // Reveal elements immediately only if they are visibly above the initial fold
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.75 && rect.bottom > 0) {
        el.classList.add('is-revealed');
      } else {
        observer.observe(el);
      }
    });
  }

  /**
   * Header Scroll State Controller (Glassmorphism + Elevation)
   */
  function initHeaderScrollDynamics() {
    const header = document.querySelector('header-component') || document.querySelector('.header');
    if (!header) return;

    let ticking = false;
    const updateHeader = () => {
      const scrolled = window.scrollY > 20;
      header.classList.toggle('is-scrolled', scrolled);
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          window.requestAnimationFrame(updateHeader);
          ticking = true;
        }
      },
      { passive: true }
    );

    updateHeader();
  }

  /**
   * Clipboard Micro-Interactions (Batch numbers, SKU copy)
   */
  function initClipboardTooltips() {
    document.addEventListener('click', (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;

      const copyBtn = target.closest('[data-copy-text], .kerva-copy-btn');
      if (!copyBtn) return;

      const textToCopy = copyBtn.getAttribute('data-copy-text') || copyBtn.textContent.trim();
      if (!textToCopy) return;

      navigator.clipboard.writeText(textToCopy).then(() => {
        let tooltip = copyBtn.querySelector('.kerva-tooltip');
        if (!tooltip) {
          tooltip = document.createElement('span');
          tooltip.className = 'kerva-tooltip';
          tooltip.setAttribute('role', 'status');
          copyBtn.appendChild(tooltip);
        }

        tooltip.textContent = 'Copied! ✓';
        tooltip.classList.add('is-active');

        setTimeout(() => {
          tooltip.classList.remove('is-active');
        }, 1800);
      }).catch(() => {
        // Fallback or permission denial handled gracefully
      });
    });
  }

  /**
   * Tactile Quantity Controls Feedback
   */
  function initQuantityFeedback() {
    document.addEventListener('pointerdown', (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      const btn = target.closest('.quantity-minus, .quantity-plus, .quantity-selector button, quantity-selector-component button');
      if (!btn) return;
      btn.style.transform = 'scale(0.78)';
    });

    const resetTransform = (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      const btn = target.closest('.quantity-minus, .quantity-plus, .quantity-selector button, quantity-selector-component button');
      if (!btn) return;
      setTimeout(() => {
        btn.style.transform = '';
      }, 100);
    };

    document.addEventListener('pointerup', resetTransform);
    document.addEventListener('pointercancel', resetTransform);
  }

  // Lifecycle Initialization
  function initAll() {
    initMotionObserver();
    initHeaderScrollDynamics();
    initClipboardTooltips();
    initQuantityFeedback();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.addEventListener('load', initAll);

  // Support Shopify Theme Editor dynamic re-renders & section changes
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:reorder', initAll);

  // Support View Transitions / MPA page reveals
  window.addEventListener('pagereveal', () => {
    requestAnimationFrame(initAll);
  });
})();
